import PropTypes from 'prop-types';
import React, { Fragment, useCallback } from 'react';
import showLiveSupport from '@xanadu/ui-shared/utils/showLiveSupport';

import {
  CASHIER_STATUSES,
  DOCUMENT_STATUSES,
  EDD_DOCUMENT_STATUSES_TO_VERIFY,
} from '@xanadu/ui-shared/modules/user/data';

import { IRELAND_COUNTRY_CODE, UK_COUNTRY_CODE } from '../../constants';

import VerificationNeeded from './components/VerificationNeeded';
import ReVerificationNeeded from './components/ReVerificationNeeded';
import Rejected from './components/Rejected';
import RegistrationSuccess from './components/RegistrationSuccess';
import AdditionalDocsNeeded from './components/AdditionalDocsNeeded';
import EddVerification from './components/EddVerification';
import CashierRestricted from './components/CashierRestricted';
import EddVerificationRestricted from './components/EddVerificationRestricted';
import KycCashierRestricted from './components/KycCashierRestricted';

import styles from './KycMessaging.module.scss';

const {
  VERIFICATION_NEEDED,
  PENDING,
  SUSPENDED,
  PASSED_ADDITIONAL_DOCS,
} = CASHIER_STATUSES;

const {
  NOT_SENT,
  REJECTED,
  ADDITIONAL_DOCS,
  RE_VERIFICATION_NEEDED,
} = DOCUMENT_STATUSES;

const KycMessaging = ({
  history,
  signUpSuccess = false,
  closeModal,
  displayModal,
  depositRequest,
  widthSize,
  proofOfIdentityStatus: POIStatus,
  proofOfAddressStatus: POAStatus,
  eddStatus,
  extraData,
  cashierStatus,
  countryId
}) => {
  const isDesktop = widthSize >= 768;

  const verifyCashier = extraData.get('verifyCashier');

  const IS_UK_USER = countryId === UK_COUNTRY_CODE;

  const isIrishCustomer = countryId === IRELAND_COUNTRY_CODE;

  // Priority 0
  const showEdd = EDD_DOCUMENT_STATUSES_TO_VERIFY.includes(eddStatus);

  // Priority 1
  const showNotSent = !IS_UK_USER && [POIStatus, POAStatus].includes(NOT_SENT);

  // Priority 2
  const showRejected = [POIStatus, POAStatus].includes(REJECTED)
    && !showNotSent;

  // Priority 3
  const showAdditionalDocs = [POIStatus, POAStatus].includes(ADDITIONAL_DOCS)
    && !showRejected && !showNotSent;

  // Priority 4
  const showReVerify = [POIStatus, POAStatus].includes(RE_VERIFICATION_NEEDED)
    && !showRejected && !showAdditionalDocs && !showNotSent;

  const showPOIDoc =
    showNotSent && POIStatus === NOT_SENT
    || showRejected && POIStatus === REJECTED
    || showReVerify && POIStatus === RE_VERIFICATION_NEEDED;

  const showPOADoc =
    showNotSent && POAStatus === NOT_SENT
    || showRejected && POAStatus === REJECTED
    || showReVerify && POAStatus === RE_VERIFICATION_NEEDED;

  // Priority of the deposit popups logic
  const showCashierRestricted = cashierStatus === SUSPENDED;

  const showEddStatus = eddStatus === PASSED_ADDITIONAL_DOCS
    && !showCashierRestricted;

  const showKycCashierRestricted = [VERIFICATION_NEEDED, PENDING].includes(cashierStatus)
    && !showCashierRestricted && !showEddStatus;

  const uploadDocuments = () => {
    closeModal();

    const timer = setTimeout(() => {
      const userRegistered = !verifyCashier;
      displayModal({
        show: true,
        section: !isIrishCustomer ? 'upload-documents' : 'grai-verification',
        data: {
          userRegistered
        }
      });
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  };

  const redirectToApps = useCallback(() => {
    closeModal();

    const timer = setTimeout(() => {
      history.push('/page/about/mobile-applications');
    }, 240);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const onDepositNow = useCallback(() => {
    closeModal();

    const timer = setTimeout(() => {
      depositRequest();
    }, 240);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const onShowLiveChat = () => {
    closeModal();
    showLiveSupport();
  };

  return (
    <div className={styles.main}>
      {signUpSuccess ? (
        <RegistrationSuccess
          depositRequest={onDepositNow}
          closeModal={closeModal}
        />
      ) : (
        <Fragment>
          {!verifyCashier ?
            <Fragment>
              {showEdd ? (
                <EddVerification
                  closeModal={closeModal}
                  navigate={history.push}
                  eddStatus={eddStatus}
                />
              ) : (
                <Fragment>
                  {showNotSent && (
                    <VerificationNeeded
                      isDesktop={isDesktop}
                      closeModal={closeModal}
                      uploadDocuments={uploadDocuments}
                      redirectToApps={redirectToApps}
                      showPOIDoc={showPOIDoc}
                      showPOADoc={showPOADoc}
                    />
                  )}
                  {showRejected && (
                    <Rejected
                      isDesktop={isDesktop}
                      uploadDocuments={uploadDocuments}
                      redirectToApps={redirectToApps}
                      onShowLiveChat={onShowLiveChat}
                      showPOIDoc={showPOIDoc}
                      showPOADoc={showPOADoc}
                    />
                  )}
                  {showAdditionalDocs && (
                    <AdditionalDocsNeeded
                      isDesktop={isDesktop}
                      uploadDocuments={uploadDocuments}
                      redirectToApps={redirectToApps}
                      onShowLiveChat={onShowLiveChat}
                    />
                  )}
                  {showReVerify && (
                    <ReVerificationNeeded
                      isDesktop={isDesktop}
                      uploadDocuments={uploadDocuments}
                      redirectToApps={redirectToApps}
                      closeModal={closeModal}
                      showPOIDoc={showPOIDoc}
                      showPOADoc={showPOADoc}
                    />
                  )}
                </Fragment>
              )}
            </Fragment>
            :
            <Fragment>
              {showEddStatus && (
                <EddVerificationRestricted
                  closeModal={closeModal}
                  onShowLiveChat={onShowLiveChat}
                />
              )}
              {showCashierRestricted && (
                <CashierRestricted
                  closeModal={closeModal}
                  onShowLiveChat={onShowLiveChat}
                />
              )}
              {showKycCashierRestricted && (
                <KycCashierRestricted
                  isDesktop={isDesktop}
                  uploadDocuments={uploadDocuments}
                  redirectToApps={redirectToApps}
                  closeModal={closeModal}
                />
              )}
            </Fragment>
          }
        </Fragment>
      )}
    </div>
  );
};

KycMessaging.propTypes = {
  closeModal: PropTypes.func.isRequired,
  extraData: PropTypes.object,
  displayModal: PropTypes.func.isRequired,
  depositRequest: PropTypes.func.isRequired,
  widthSize: PropTypes.number.isRequired,
  proofOfIdentityStatus: PropTypes.string,
  proofOfAddressStatus: PropTypes.string,
  eddStatus: PropTypes.string,
  cashierStatus: PropTypes.string,
  signUpSuccess: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  countryId: PropTypes.number
};

export default KycMessaging;

